import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type RowClassParams } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
}

const defState = {
};

interface SearchParams {
  statDateFrom: string,
  statDateTo: string,
  statTimeFrom: string,
  statTimeTo: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams = {
  statDateFrom: dayjs().subtract(1, "month").format("YYYYMMDD"),
  statDateTo: dayjs().format("YYYYMMDD"),
  statTimeFrom: "0",
  statTimeTo: "24",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

function ResultSummary(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "일자", field: "statDate", width: 150, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        return p.node.rowPinned ? p.data.statDate : kutil.date2string(p.data.statDate, "yyyy-MM-dd");
      },
      summaryLabel: "합계"
    },
    { headerName: "기준시간", field: "statTime", width: 150, cellStyle: {textAlign: "center"} },
    {
      headerName: "설비작업",
      children: [
        { headerName: "입고", field: "equipErpInQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "출고", field: "equipErpOutQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "BackUp입고", field: "equipEtcInQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "BackUp출고", field: "equipEtcOutQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "재고실사", field: "auditQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "소계", field: "", width: 100, cellStyle: { textAlign: "right"},
          valueGetter: (p: any) => {
            return p.data.equipErpInQty + p.data.equipErpOutQty + p.data.equipEtcInQty + p.data.equipEtcOutQty + p.data.auditQty;
          },
        },
      ]
    },
    {
      headerName: "작업지시",
      children: [
        { headerName: "입고지시", field: "erpInQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "출고지시", field: "erpOutQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "BackUp입고지시", field: "etcInQty", width: 120, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "BackUp출고지시", field: "etcOutQty", width: 120, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, summable: true },
        { headerName: "소계", field: "", width: 100, cellStyle: { textAlign: "right"},
          valueGetter: (p: any) => {
            return p.data.erpInQty + p.data.erpOutQty + p.data.etcInQty + p.data.etcOutQty;
          },
        },
      ]
    },
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
  }), []);
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/report/result_summary.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/report/result_summary", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/report/result_summary", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/report/result_summary", {});
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/report/result_summary.download?mode=search", searchParams, {
        "fileName": "종합실적_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>종합실적</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div className="date-range">
            <p>조회일자</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={searchParams.statDateFrom ? dayjs(searchParams.statDateFrom, "YYYYMMDD") : null}
                  onChange={(e) => {doSearch(e, {statDateFrom: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  maxDate={dayjs(searchParams.statDateTo)}
                />
                -
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={searchParams.statDateTo ? dayjs(searchParams.statDateTo, "YYYYMMDD") : null}
                  onChange={(e) => {doSearch(e, {statDateTo: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  minDate={dayjs(searchParams.statDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <div>
            <p>기준시간</p>
            <div className="form-div unit02-range">
              <FormControl>
                <Select
                  value={searchParams.statTimeFrom}
                  onChange={(e) => {doSearch(e, {statTimeFrom: e.target.value});}}
                  displayEmpty
                >
                  {Array.from({ length: 25 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <span>-</span>
              <FormControl>
                <Select
                  value={searchParams.statTimeTo}
                  onChange={(e) => {doSearch(e, {statTimeTo: e.target.value});}}
                  displayEmpty
                >
                  {Array.from({ length: 25 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 종합실적 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              summaryPosition="top"
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 종합실적 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(ResultSummary);
